import {
  EmptyState,
  FlexBox,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Spinner,
} from '@dixa/lib-shared-habla';
import type { Conversation } from 'lib-shared-legacy-types';
import { TagChips } from 'lib-shared-tagging';
import { useCallback, useEffect, useState } from 'react';
import { useDebounce } from 'react-use';

import {
  ConversationContextProvider,
  ConversationEventGroups,
  ConversationSummaryByCsid,
} from 'components/conversation';
import { ConversationEventGroupsGroupConfig } from 'components/conversation/conversationEventGroups/conversationEventGroups.types';
import { useReadPatchedConversation } from 'utils/conversation/useReadPatchedConversation';
import { createAppModal } from 'utils/modal';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import styles from './conversationPreviewModal.module.scss';

/** The config for which event groups to present in the preview. */
const groupsConfig: readonly ConversationEventGroupsGroupConfig[] = [
  'group:pstn_phone',
  'group:voicemail',
  {
    'group:contact_form': { hideAttachments: true, hideGutterActions: true },
    'group:email': { hideAttachments: true, hideGutterActions: true },
    'group:messenger': { hideAttachments: true, hideGutterActions: true },
    'group:adachat': { hideAttachments: true, hideGutterActions: true },
    'group:digitalgeniuschat': {
      hideAttachments: true,
      hideGutterActions: true,
    },
    'group:kindlychat': { hideAttachments: true, hideGutterActions: true },
    'group:trustpilotreviews': {
      hideAttachments: true,
      hideGutterActions: true,
    },
    'group:widget_chat': { hideAttachments: true, hideGutterActions: true },
    'group:facebook_messenger': {
      hideAttachments: true,
      hideGutterActions: true,
    },
    'group:whats_app': { hideAttachments: true, hideGutterActions: true },
    'group:sms': { hideAttachments: true, hideGutterActions: true },
    'group:twitter': { hideAttachments: true, hideGutterActions: true },
    'group:instagram_story': { hideGutterActions: true },
    'group:conversation:wrapup_message': { hideGutterActions: true },
  },
];

/** Represents the modal for previewing a conversation. */
export const ConversationPreviewModal = createAppModal(
  { type: 'app:conversationPreview' },
  ({ isOpen, modal, hide }): JSX.Element => {
    const navigate = useAppNavigate();
    const [isFulfilled, setIsFulfilled] = useState(false);
    const [readConversation, { data: conversationData, asyncStatus }] =
      useReadPatchedConversation();

    const mainConversation = conversationData?.data.history.main_conversation;

    /** Handles closing the modal. */
    const handleClose = useCallback(() => {
      setIsFulfilled(false);
      hide?.();
    }, [hide]);

    /** Handles navigating to the currently previewed conversation. */
    const handleViewConversation = useCallback(() => {
      navigate({
        path: '/conversation/:csid',
        params: { path: { csid: modal.arg.csid.toString() } },
      });

      handleClose();
    }, [handleClose, modal, navigate]);

    // Handles fetching the conversation.
    useEffect(() => {
      if (isOpen) {
        readConversation({ csid: modal.arg.csid });
      }
    }, [isOpen, modal.arg.csid, readConversation]);

    // Handles delaying fulfilled state to prevent elements being switched too
    // quickly.
    useDebounce(
      () => {
        if (isOpen) {
          setIsFulfilled(asyncStatus === 'fulfilled');
        }
      },
      300,
      [asyncStatus, isOpen],
    );

    return (
      <Modal
        isOpen={isOpen}
        className={styles.root}
        onClose={handleClose}
        allowClickOutside
      >
        <ModalHead
          title={`Conversation #${modal.arg.csid} preview`}
          onClose={handleClose}
        />

        <ModalBody>
          {isFulfilled && mainConversation ? (
            <>
              {mainConversation.tags.length > 0 && (
                <FlexBox margin="0 0 300">
                  <TagChips tags={mainConversation.tags} />
                </FlexBox>
              )}

              {mainConversation.summary.can_be_generated && (
                <ConversationContextProvider
                  conversation={mainConversation as unknown as Conversation}
                >
                  <ConversationSummaryByCsid
                    csid={mainConversation.id}
                    view="preview"
                    className={styles.summary}
                  />
                </ConversationContextProvider>
              )}

              <ConversationEventGroups
                mainConversation={mainConversation}
                groupsConfig={groupsConfig}
                emptyMessage={
                  <EmptyState
                    title="Nothing to preview"
                    description="There are no messages to preview in the conversation."
                  />
                }
              />
            </>
          ) : (
            <FlexBox justifyContent="center" alignItems="center" height="100px">
              <Spinner size={32} />
            </FlexBox>
          )}
        </ModalBody>

        <ModalFoot
          cancelName="Close"
          cancelOnClick={handleClose}
          confirmName="View conversation"
          confirmOnClick={handleViewConversation}
        />
      </Modal>
    );
  },
);
